import React, { useEffect, useMemo, useState } from "react";
import SideBar from "./SideBar";
import axiosClient from "../axios";
import Pagination_2 from "./Pagination_2";
import loader from "../assets/images/loader.gif";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Autocomplete, TextField } from "@mui/material";
import "../assets/css/AdminCommon.css";

const ACTION_OPTIONS = [
  { name: "All Actions", value: "" },
  { name: "Impression", value: "impression" },
  { name: "Clicked", value: "clicked" },
  { name: "Closed", value: "closed" },
];

const ACTION_BADGE_CLASS = {
  impression: "status-3",
  clicked: "status-1",
  closed: "status-2",
};

const getBannerId = (row) =>
  typeof row?.bannerId === "string" ? row.bannerId : row?.bannerId?._id ?? "";

const getUserId = (row) =>
  typeof row?.userId === "string" ? row.userId : row?.userId?._id ?? "";

const CSV_COLUMNS = [
  { header: "S.No", getValue: (_row, index) => index + 1 },
  { header: "Activity ID", getValue: (row) => row?._id ?? "" },
  { header: "Banner ID", getValue: (row) => getBannerId(row) },
  { header: "Banner Display Mode", getValue: (row) => row?.bannerId?.displayMode ?? "" },
  { header: "User ID", getValue: (row) => getUserId(row) },
  { header: "User Name", getValue: (row) => row?.userId?.name ?? "" },
  { header: "User Email", getValue: (row) => row?.userId?.emailId ?? "" },
  { header: "User Phone Number", getValue: (row) => row?.userId?.phoneNumber ?? "" },
  { header: "Action", getValue: (row) => row?.action ?? "" },
  { header: "Screen Position", getValue: (row) => row?.screenPosition ?? "" },
  {
    header: "Created At (Local)",
    getValue: (row) =>
      row?.created_at
        ? moment.utc(row.created_at).local().format("DD/MM/YYYY, h:mm:ss a")
        : "",
  }
];

const escapeCsvValue = (value) => {
  const str = value === null || value === undefined ? "" : String(value);
  return `"${str.replace(/"/g, '""')}"`;
};

const buildCsv = (rows) => {
  const header = CSV_COLUMNS.map((col) => escapeCsvValue(col.header)).join(",");
  const body = rows.map((row, index) =>
    CSV_COLUMNS.map((col) => escapeCsvValue(col.getValue(row, index))).join(",")
  );
  return [header, ...body].join("\r\n");
};

const triggerCsvDownload = (csvString, fileName) => {
  const blob = new Blob(["\uFEFF" + csvString], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function AllBannerUserActivity() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [hasNext, setHasNext] = useState(false);

  const [bannerOptionsList, setBannerOptionsList] = useState([]);
  const [bannerOptionsLoading, setBannerOptionsLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadCount, setDownloadCount] = useState("");
  const [downloading, setDownloading] = useState(false);

  const [filters, setFilters] = useState({
    userId: searchParams.get("userId") || "",
    bannerId: searchParams.get("bannerId") || "",
    action: searchParams.get("action") || "",
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    userId: searchParams.get("userId") || "",
    bannerId: searchParams.get("bannerId") || "",
    action: searchParams.get("action") || "",
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
  });

  const [pagination, setPagination] = useState({
    page: 0,
    limit: 50,
  });

  const fetchActivities = () => {
    setLoading(true);

    axiosClient
      .post("/admin/clickbait/activity/all", {
        page: pagination.page,
        limit: pagination.limit,
        userId: appliedFilters.userId,
        bannerId: appliedFilters.bannerId,
        action: appliedFilters.action,
        startDate: appliedFilters.startDate,
        endDate: appliedFilters.endDate,
      })
      .then((res) => {
        if (res?.data?.statusCode === 200) {
          const result = res?.data?.response || [];
          setActivities(result);
          setHasNext(result.length === pagination.limit);
        }
      })
      .catch((error) => {
        console.error(error);
        alert(error?.response?.data?.message || "Error fetching banner user activity");
      })
      .finally(() => setLoading(false));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    const cleared = { userId: "", bannerId: "", action: "", startDate: "", endDate: "" };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPagination({ page: 0, limit: 50 });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    fetchActivities();
  }, [pagination.page, pagination.limit, appliedFilters]);

  useEffect(() => {
    setBannerOptionsLoading(true);
    axiosClient
      .post("/admin/clickbait/list", {})
      .then((res) => {
        if (res?.data?.statusCode === 200) {
          setBannerOptionsList(res?.data?.response || []);
        }
      })
      .catch((error) => {
        console.error("Error fetching banner list:", error);
      })
      .finally(() => setBannerOptionsLoading(false));
  }, []);

  const bannerSelectOptions = useMemo(
    () => [{ _id: "", imageUrl: "" }, ...bannerOptionsList],
    [bannerOptionsList]
  );

  const closeDownloadModal = () => {
    if (downloading) return;
    setShowDownloadModal(false);
    setDownloadCount("");
  };

  const handleDownloadCsv = async () => {
    const requestedCount = Number(downloadCount);

    if (!Number.isInteger(requestedCount) || requestedCount <= 0) {
      alert("Please enter a valid whole number greater than 0");
      return;
    }

    setDownloading(true);

    try {
      const payload = {
        page: 0,
        limit: requestedCount,
        userId: appliedFilters.userId,
        bannerId: appliedFilters.bannerId,
        action: appliedFilters.action,
        startDate: appliedFilters.startDate,
        endDate: appliedFilters.endDate,
      };

      const res = await axiosClient.post("/admin/clickbait/activity/all", payload);

      if (res?.data?.statusCode !== 200) {
        alert(res?.data?.message || "Failed to fetch records");
        return;
      }

      const rows = res?.data?.response || [];

      if (rows.length === 0) {
        alert("No records found for the selected filters");
        return;
      }

      const csv = buildCsv(rows);
      triggerCsvDownload(
        csv,
        `banner-user-activity-${moment().format("YYYYMMDD-HHmmss")}.csv`
      );

      setShowDownloadModal(false);
      setDownloadCount("");

      if (rows.length < requestedCount) {
        alert(
          `Only ${rows.length} record(s) available. Downloaded all ${rows.length}.`
        );
      }
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || "Error downloading CSV");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <SideBar handleSideBar={true} activeTab={"BannerUserActivity"} />

      <div className="admin-screen-v4">
        <div className="admin-header-v4">
          <div>
            <h2>Banner User Activity</h2>
            <p style={{ margin: "8px 0 0 0", fontSize: "14px", color: "var(--text-muted)" }}>
              Track impressions, clicks, and closes on custom banners
            </p>
          </div>

          <div style={{ display: "flex", gap: "var(--spacing-md)", alignItems: "center" }}>
            <button
              className="submit-button-v4"
              onClick={() => setShowDownloadModal(true)}
            >
              Download CSV
            </button>
            <button
              className="btn-secondary-v4"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-lg)" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "var(--spacing-lg)",
                alignItems: "end",
              }}
            >
              <div className="form-group-v4">
                <label className="form-label-v4">User ID</label>
                <input
                  type="text"
                  name="userId"
                  className="form-input-v4"
                  placeholder="Filter by user ID"
                  value={filters.userId}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="form-group-v4">
                <label className="form-label-v4">Banner</label>
                <Autocomplete
                  options={bannerSelectOptions}
                  loading={bannerOptionsLoading}
                  value={
                    bannerSelectOptions.find((option) => option._id === filters.bannerId) ||
                    bannerSelectOptions[0]
                  }
                  onChange={(_, newValue) =>
                    setFilters((prev) => ({ ...prev, bannerId: newValue?._id || "" }))
                  }
                  getOptionLabel={(option) => option._id || "All Banners"}
                  isOptionEqualToValue={(option, val) => option._id === val._id}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id || "all"}>
                      {option._id ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <img
                            src={option.imageUrl}
                            alt="banner"
                            style={{
                              width: "32px",
                              height: "32px",
                              objectFit: "contain",
                              borderRadius: "var(--radius-sm)",
                              border: "1px solid var(--border-color)",
                            }}
                          />
                          <span style={{ fontSize: "12px" }}>{option._id}</span>
                        </div>
                      ) : (
                        "All Banners"
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select banner" size="small" />
                  )}
                  size="small"
                />
              </div>

              <div className="form-group-v4">
                <label className="form-label-v4">Action</label>
                <Autocomplete
                  options={ACTION_OPTIONS}
                  value={
                    ACTION_OPTIONS.find((option) => option.value === filters.action) ||
                    ACTION_OPTIONS[0]
                  }
                  onChange={(_, newValue) =>
                    setFilters((prev) => ({ ...prev, action: newValue?.value || "" }))
                  }
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select action" size="small" />
                  )}
                  size="small"
                />
              </div>

              <div className="form-group-v4">
                <label className="form-label-v4">Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  className="form-input-v4"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="form-group-v4">
                <label className="form-label-v4">End Date</label>
                <input
                  type="date"
                  name="endDate"
                  className="form-input-v4"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                />
              </div>

              <div style={{ display: "flex", gap: "var(--spacing-md)" }}>
                <button className="submit-button-v4" onClick={handleApplyFilters}>
                  Apply Filters
                </button>
                <button className="btn-secondary-v4" onClick={handleClearFilters}>
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="admin-form-container-v4">
          {!loading ? (
            <>
              {activities.length > 0 ? (
                <>
                  <div style={{ marginTop: "var(--spacing-xl)" }}>
                    <Pagination_2
                      filterDataInput={pagination}
                      setFilterDataInput={setPagination}
                      showNext={hasNext}
                    />
                  </div>
                  <div className="table-container-v4">
                    <table className="data-table-v4">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>User</th>
                          <th>Action</th>
                          <th>Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activities.map((activity, index) => (
                          <tr key={activity._id || index}>
                            <td>{pagination.limit * pagination.page + (index + 1)}</td>
                            <td>
                              <div
                                className="text-link-v4"
                                style={{ cursor: activity?.userId?._id ? "pointer" : "default" }}
                                onClick={() =>
                                  activity?.userId?._id &&
                                  navigate(`/users/transaction/${activity.userId._id}`)
                                }
                              >
                                <div>{activity?.userId?.name || "N/A"}</div>
                                {activity?.userId?.emailId && (
                                  <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                                    {activity.userId.emailId}
                                  </div>
                                )}
                                {activity?.userId?.phoneNumber && (
                                  <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                                    {activity.userId.phoneNumber}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <span
                                className={`status-badge-v4 ${
                                  ACTION_BADGE_CLASS[activity?.action] || ""
                                }`}
                              >
                                {activity?.action || "N/A"}
                              </span>
                            </td>
                            <td>
                              {activity?.created_at
                                ? moment
                                    .utc(activity.created_at)
                                    .local()
                                    .format("DD/MM/YY, h:mm:ss a")
                                : "N/A"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="empty-state-v4">
                  <div className="empty-icon-v4">📊</div>
                  <h3>No Activity Found</h3>
                  <p>No banner user activity matches the selected filters</p>
                </div>
              )}
            </>
          ) : (
            <div className="loader-container-v4">
              <img src={loader} alt="Loading..." className="loader-image-v4" />
            </div>
          )}
        </div>
      </div>

      {showDownloadModal && (
        <div
          onClick={closeDownloadModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "16px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--card-bg, #fff)",
              borderRadius: "var(--radius-md, 12px)",
              padding: "var(--spacing-xl, 24px)",
              width: "100%",
              maxWidth: "420px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
            }}
          >
            <h3 style={{ margin: "0 0 8px 0" }}>Download CSV</h3>
            <p
              style={{
                margin: "0 0 20px 0",
                fontSize: "13px",
                color: "var(--text-muted)",
              }}
            >
              Enter how many records you want to download. The currently applied
              filters will be used.
            </p>

            <div className="form-group-v4">
              <label className="form-label-v4">Number of Records</label>
              <input
                type="number"
                min="1"
                step="1"
                className="form-input-v4"
                placeholder="e.g. 100"
                value={downloadCount}
                onChange={(e) => setDownloadCount(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !downloading) handleDownloadCsv();
                  if (e.key === "Escape") closeDownloadModal();
                }}
                autoFocus
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "var(--spacing-md)",
                marginTop: "var(--spacing-lg, 20px)",
              }}
            >
              <button
                className="btn-secondary-v4"
                onClick={closeDownloadModal}
                disabled={downloading}
              >
                Cancel
              </button>
              <button
                className="submit-button-v4"
                onClick={handleDownloadCsv}
                disabled={downloading}
              >
                {downloading ? "Downloading..." : "Download"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AllBannerUserActivity;